<template>
  <lf-modal
    :title="`${$t('COMMON.CHANGE')} ${title}`"
    title-border
    :close="close"
  >
    <template #content>
      <div class="space-y-5 p-6">
        <div class="space-y-4 rounded-4 bg-yellow-100/80 p-4">
          <div
            v-if="showStatusChangeMessage"
            data-cy="status-change-message"
            class="flex items-center space-x-4"
          >
            <lf-chip type="warning" />
            <lf-h3>
              {{
                $t("DEALS.STATUS_UPDATE_MESSAGE", {
                  status: selectedSubStatusCategory,
                })
              }}
            </lf-h3>
          </div>
          <div class="flex items-center space-x-4">
            <lf-chip type="warning" />
            <lf-h3>
              {{
                $t("DEALS.TABLE_MULTISELECT.UPDATE_MESSAGE", {
                  field: capitalize(title),
                })
              }}
            </lf-h3>
          </div>
        </div>
        <div class="space-y-4">
          <div
            v-if="showSubStatusesSelector"
            class="space-y-4"
            data-cy="sub-statuses-selector"
          >
            <lf-h3>
              {{ `${$t("COMMON.SELECT")} ${$t("COMMON.SUB_STATUS")}` }}
            </lf-h3>
            <lf-dropdown
              :options="{
                null: t('COMMON.NONE'),
                ...subStatuses,
              }"
              :value="selectedSubStatus"
              :placeholder="t('COMMON.SELECT')"
              :disabled="isSubmitting"
              @change="(item) => (selectedSubStatus = item.toString())"
            />
          </div>
          <lf-h2>{{ $t("COMMON.DEALS") }}</lf-h2>
          <div class="max-h-40 space-y-3 overflow-y-auto">
            <div v-for="deal in selectedDeals" :key="deal.id">
              <mode-badges
                v-if="data.selected.key === DATA_OPTIONS.DEAL_MODE"
                :deal="deal"
                :selected-value="String(data.selected.item.value || '')"
              />
              <value-badges
                v-else
                :deal="deal"
                :selected-value="data.selected"
              />
            </div>
          </div>
        </div>
      </div>
      <hr class="my-4" />
      <div class="flex justify-end">
        <modal-buttons
          class="bg-white py-5"
          :disabled="isSubmitting"
          :on-cancel="close"
          :on-save="handleSave"
        />
      </div>
    </template>
  </lf-modal>
</template>
<script lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import type { IApplication, MultiselectModalData } from "@/models/applications";
</script>
<script setup lang="ts">
import capitalize from "lodash/capitalize";

import { DATA_OPTIONS } from "@/helpers/constants/deals";

import ModeBadges from "@/components/deals/multiselectControls/ModeBadges.vue";
import ValueBadges from "@/components/deals/multiselectControls/ValueBadges.vue";
import LfChip from "@/components/ui/LfChip.vue";
import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfH2 from "@/components/ui/text/LfHeading2.vue";
import LfH3 from "@/components/ui/text/LfHeading3.vue";

const {
  data,
  close,
  subStatuses = {},
} = defineProps<{
  data: MultiselectModalData;
  close: () => void;
  subStatuses: Record<string, string>;
}>();

const emit = defineEmits<{
  save: [Record<string, string>];
}>();

const { getters } = useStore();
const { t } = useI18n();

const isSubmitting = ref(false);
const selectedSubStatus = ref<string>("null");

const title = computed(() => {
  const titleOptions = {
    [DATA_OPTIONS.ADVISOR]: t("COMMON.ONBOARDER"),
    [DATA_OPTIONS.ANALYST]: t("COMMON.ANALYST"),
    [DATA_OPTIONS.UNDERWRITER]: t("COMMON.PROCESSOR"),
    [DATA_OPTIONS.DEAL_MODE]: `${t("COMMON.DEAL")} ${t("COMMON.MODE")}`,
    [DATA_OPTIONS.WORKFLOW_STATUS]:
      selectedSubStatus.value === "null"
        ? t("COMMON.STATUS")
        : `${t("COMMON.STATUS")} ${t("COMMON.AND")} ${t("COMMON.SUB_STATUS")}`,
    [DATA_OPTIONS.WORKFLOW_SUB_STATUS]: t("COMMON.SUB_STATUS"),
  };

  return titleOptions[data.selected.key];
});

const deals = computed<IApplication[]>(() => getters["applications/all"]);

const showSubStatusesSelector = computed(
  () => !!Object.keys(subStatuses).length
);

const showStatusChangeMessage = computed(
  () => data.selected.key === DATA_OPTIONS.WORKFLOW_SUB_STATUS
);

const selectedDeals = computed(() =>
  deals.value.filter(({ id }) => data.deals.includes(id))
);

const selectedSubStatusCategory = computed(
  () => data.selected.item.id?.toString().split("__")[0]
);

const handleSave = () => {
  emit("save", {
    selectedSubStatus: selectedSubStatus.value,
  });
  close();
};
</script>
